/**
 * Upright relief models for the campaign atlas. The ground stays continuous;
 * these irregular stone masses supply the visible height above it. All meshes
 * are shared SVG definitions, with one use element per terrain mark.
 */
import { coastPath, VIEW, warp, type TerrainMark } from '../data/cartography.ts';
import { hash2, RASTER } from '../data/realm.ts';
import { provinceDef } from '../data/provinces.ts';
import { mix, toHex } from '../data/palette.ts';
import { snowAt } from './relief.ts';

type Point = readonly [number, number];
type Stone = { dark: string; shade: string; middle: string; light: string; edge: string };

const MOUNTAIN: Stone = {
  dark: '#353f3c', shade: '#505b58', middle: '#7e8175', light: '#a49d86', edge: '#c0b18f',
};
const HILL: Stone = {
  dark: '#41483b', shade: '#535c45', middle: '#70785c', light: '#878969', edge: '#a29a79',
};
const VARIANTS = 8;

function n(value: number): string { return String(Math.round(value * 100) / 100); }
function path(points: readonly Point[], fill: string, opacity = 1): string {
  return facets([points], fill, opacity);
}

/** Many small faces share one material path inside the cached definition. */
function facets(polygons: readonly (readonly Point[])[], fill: string, opacity = 1): string {
  const d = polygons.map((points) => `M${points.map(([x, y]) => `${n(x)} ${n(y)}`).join('L')}Z`).join('');
  return `<path d="${d}" style="fill:${fill};stroke:none"${opacity < 1 ? ` opacity="${n(opacity)}"` : ''}/>`;
}

/** Three overlapping summits share one jagged skyline and connected foothills. */
function stoneMesh(variant: number, hill: boolean): { body: string; detail: string } {
  const color = hill ? HILL : MOUNTAIN;
  const heights = [3.8, 4.35, 3.5, 4.08, 3.72, 4.5, 3.96, 4.24];
  const h = hill ? 1.42 + (variant % 3) * 0.23 : heights[variant] * 1.18;
  const lean = [-0.42, 0.17, -0.08, 0.42, -0.2, 0.04, -0.53, 0.28][variant];
  const spread = hill ? 0.98 : 1;
  const p = (x: number, y: number): Point => [x * spread, y];

  const left = p(-3.22, 0.38);
  const westKnee = p(-2.77, -h * 0.28);
  const westPeak = p(-1.94 - (variant % 3) * 0.09, -h * (0.64 + (variant % 2) * 0.16));
  const westLip = p(-1.73, -h * 0.55);
  const westSaddle = p(-1.35, -h * 0.33);
  const knee = p(lean - 0.63, -h * 0.69);
  const top = p(lean, -h);
  const crown = p(lean + 0.39, -h * 0.8);
  const eastSaddle = p(0.86, -h * 0.41);
  const eastPeak = p(1.61 + (variant % 2) * 0.16, -h * (0.65 + (variant % 3) * 0.06));
  const eastKnee = p(2.28, -h * 0.32);
  const right = p(3.22, 0.3);
  const eastToe = p(2.36, 0.93);
  const toe = p(0.69, 1.18);
  const westToe = p(-1.32, 1.05);
  const westFoot = p(-2.53, 0.82);

  const westSpine = p(-1.82, -h * 0.12);
  const westValley = p(-0.87, -h * 0.08);
  const spine = p(lean + 0.03, -h * 0.48);
  const lowerSpine = p(-0.12, -h * 0.04);
  const forePeak = p(-0.27, -h * 0.24);
  const foreKnee = p(0.36, 0.29);
  const eastValley = p(0.98, -h * 0.13);
  const eastSpine = p(1.57, -h * 0.2);
  const eastFoot = p(1.82, 0.52);

  // The entire massif sits on one spreading ground shadow. Its ends tuck
  // under neighbouring masses when the chart places their shoulders together.
  let body = path([
    p(-2.94, 0.65), p(-0.18, 0.1), p(2.32, 0.27), p(3.94, 0.87),
    p(4.24, 1.32), p(2.91, 1.68), p(0.21, 1.55), p(-2.08, 1.21),
  ], '#19221b', hill ? 0.19 : 0.3);
  body += path([
    left, westKnee, westPeak, westLip, westSaddle, knee, top, crown,
    eastSaddle, eastPeak, eastKnee, right, eastToe, toe, westToe, westFoot,
  ], color.dark);
  body += facets([
    [left, westKnee, westPeak, westSpine, westFoot],
    [westSpine, westSaddle, knee, top, spine, lowerSpine, westToe],
    [eastSaddle, eastPeak, eastSpine, eastValley],
    [eastKnee, right, eastToe, eastFoot, eastSpine],
    [westFoot, westSpine, westToe, toe],
    [lowerSpine, eastValley, eastFoot, toe],
  ], color.middle);
  body += facets([
    [westKnee, westPeak, westSpine, p(-2.64, 0.12)],
    [westSaddle, knee, top, spine, westValley],
    [eastSaddle, eastPeak, eastSpine, eastValley],
    [westValley, forePeak, foreKnee, westToe],
    [p(1.12, 0.21), p(1.59, -h * 0.02), eastFoot, toe],
  ], color.light);
  body += facets([
    [westPeak, westLip, westSaddle, westValley, westSpine],
    [top, crown, eastSaddle, eastValley, lowerSpine, spine],
    [eastPeak, eastKnee, eastSpine],
    [forePeak, lowerSpine, eastValley, foreKnee],
    [foreKnee, eastFoot, eastToe, toe],
  ], color.shade);
  // Broken ribs split large faces into weathered planes and low branch spurs.
  body += facets([
    [knee, p(lean - 0.29, -h * 0.43), westValley, westSaddle],
    [westPeak, p(-1.83, -h * 0.41), westSpine],
    [eastPeak, p(1.37, -h * 0.3), eastSpine],
    [westFoot, p(-1.9, 0.4), westToe],
    [eastFoot, p(2.45, 0.53), eastToe],
  ], color.middle);
  body += facets([
    [top, p(lean - 0.23, -h * 0.68), p(lean - 0.04, -h * 0.76)],
    [westPeak, p(-2.28, -h * 0.43), p(-2.1, -h * 0.45)],
    [eastPeak, p(1.39, -h * 0.45), p(1.53, -h * 0.43)],
    [forePeak, p(-0.57, -h * 0.07), lowerSpine],
  ], color.edge, 0.6);

  const lines = [
    [spine, p(lean + 0.22, -h * 0.27), lowerSpine, foreKnee, toe],
    [westSaddle, westValley, p(-1.38, 0.35), westToe],
    [eastSaddle, eastValley, eastFoot],
    [westSpine, p(-2.05, 0.35), westFoot],
    [eastKnee, p(2.5, -h * 0.06), eastToe],
    [forePeak, p(-0.1, 0.49), toe],
  ];
  const d = lines.map((points) => `M${points.map(([x, y]) => `${n(x)} ${n(y)}`).join('L')}`).join('');
  const detail = `<path d="${d}" style="fill:none;stroke:${color.dark};stroke-width:.065;stroke-linejoin:bevel" opacity=".6"/>`;
  return { body, detail };
}

/** Definitions are built once at module load and reused by both map LODs. */
const DEFINITIONS = [false, true].map((hill) => {
  const kind = hill ? 'hill' : 'peak';
  return Array.from({ length: VARIANTS }, (_, variant) => {
    const mesh = stoneMesh(variant, hill);
    const id = `wm-atlas-${kind}-${variant}`;
    return `<g id="${id}">${mesh.body}</g>` +
      `<g id="${id}-near"><use href="#${id}"/>${mesh.detail}</g>`;
  }).join('');
}).join('');

/** Insert inside the chart's existing defs, once; no wrapper or CSS is needed. */
export function mapMountainDefs(): string { return DEFINITIONS; }

/** Same argument shape as worldmap's former ridgeChain. Peaks never rotate. */
export function mountainRidge(band: readonly TerrainMark[], hill: boolean, detail = false): string {
  if (band.length === 0) return '';
  // Sort by the feet so nearer masses hide the roots of those behind them,
  // including north-south ranges. Do not join summits into a leaning ribbon.
  const marks = [...band].sort((a, b) => a.y - b.y || a.x - b.x);
  return marks.map((mark) => mountainGlyph(mark.x, mark.y, mark.r, mark.v, hill, detail)).join('');
}

/** Individual version for a terrain key or a settlement's province preview. */
export function mountainGlyph(
  x: number, y: number, radius: number, variant: number, hill = false, detail = false,
): string {
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(radius) || radius <= 0) return '';
  // The existing scatter has four variants; a stable position bit extends it
  // to eight silhouettes without consuming or depending on simulation RNG.
  const index = ((Math.floor(variant) & 3) + ((Math.floor(x * 0.71 + y * 0.43) & 1) * 4)) % VARIANTS;
  const id = `wm-atlas-${hill ? 'hill' : 'peak'}-${index}${detail ? '-near' : ''}`;
  return `<use href="#${id}" transform="translate(${n(x)} ${n(y)}) scale(${n(radius)})"/>`;
}

// The campaign uses this continuous surface. The small meshes above remain
// useful in the terrain key, where there is no surrounding height field.
const SURFACE_STEP = 2.8;
const SURFACE_FAR_STEP = SURFACE_STEP * 3;
const SURFACE_STROKE = 0.09;
const SURFACE_FROM = 0.38;
const SURFACE_PEAK = 16;
const COAST_INSET = 1.5;

/** Dark north-facing stone and warm lit faces, with olive soil at the roots. */
const SURFACE_INK = [
  ['#3e4936', '#4b573e', '#5b6548', '#6c7253', '#7d805f', '#8e8d6c'],
  ['#3e4940', '#4c594a', '#616c55', '#758064', '#8a8d72', '#9d9d81'],
  ['#34413e', '#46534d', '#606b5e', '#7a8070', '#959681', '#aea78f'],
  ['#333f3d', '#45524f', '#616b65', '#7c8277', '#9b9a88', '#b7af99'],
];
/** Snow keeps the same six face lights: cold shadows and near-white sunny slopes.
 * Four coverage bands retain exposed stone near the shared, ragged snowline. */
const SNOW_INK = ['#879daa', '#9eb3c0', '#bbcdd7', '#d8e4e9', '#edf3f5', '#fbfcf8'];
const SURFACE_COLORS = Array.from({ length: 4 }, (_, snow) =>
  SURFACE_INK.flatMap((material) => material.map((ink, light) =>
    toHex(mix(Number.parseInt(ink.slice(1), 16), Number.parseInt(SNOW_INK[light].slice(1), 16), snow / 3))))).flat();

interface SurfaceVertex {
  /** Warped ground position, before projecting elevation toward the north. */
  x: number;
  y: number;
  h: number;
  coast: number;
  z: number;
  snow: number;
}
interface Bounds { x0: number; y0: number; x1: number; y1: number }
interface SurfaceFace extends Bounds {
  points: Point[];
  depth: number;
  ink: number;
}
interface SurfaceChunk {
  row: number;
  column: number;
  faces: SurfaceFace[];
}
interface SurfaceBatch extends Bounds {
  ink: number;
  polygons: Point[][];
}

/** Distance to the coast, shared by every grid vertex. Eight-neighbour
 * chamfer distances are enough for an inset; the chart's exact land clip is
 * still the final mask. Coast tapering also keeps tall projected slopes from
 * being abruptly cut off at the sea. This allocation dies after the bake. */
function coastDistances(): Float32Array {
  const { cols, rows, cell, owner } = RASTER;
  const result = new Float32Array(owner.length);
  const diagonal = cell * Math.SQRT2;
  for (let k = 0; k < result.length; k++) result[k] = owner[k] < 0 ? 0 : 1e5;
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const k = y * cols + x;
      if (x > 0) result[k] = Math.min(result[k], result[k - 1] + cell);
      if (y > 0) {
        result[k] = Math.min(result[k], result[k - cols] + cell);
        if (x > 0) result[k] = Math.min(result[k], result[k - cols - 1] + diagonal);
        if (x + 1 < cols) result[k] = Math.min(result[k], result[k - cols + 1] + diagonal);
      }
    }
  }
  for (let y = rows - 1; y >= 0; y--) {
    for (let x = cols - 1; x >= 0; x--) {
      const k = y * cols + x;
      if (x + 1 < cols) result[k] = Math.min(result[k], result[k + 1] + cell);
      if (y + 1 < rows) {
        result[k] = Math.min(result[k], result[k + cols] + cell);
        if (x > 0) result[k] = Math.min(result[k], result[k + cols - 1] + diagonal);
        if (x + 1 < cols) result[k] = Math.min(result[k], result[k + cols + 1] + diagonal);
      }
    }
  }
  return result;
}

/** RASTER.height is stored at cell centres, not the raster's corner. */
function sampleSurface(field: Float32Array, x: number, y: number): number {
  const { cols, rows, cell, originX, originY } = RASTER;
  const fx = Math.max(0, Math.min(cols - 1.00001, (x - originX) / cell - 0.5));
  const fy = Math.max(0, Math.min(rows - 1.00001, (y - originY) / cell - 0.5));
  const ix = Math.floor(fx), iy = Math.floor(fy);
  const tx = fx - ix, ty = fy - iy;
  const k = iy * cols + ix;
  return (field[k] * (1 - tx) + field[k + 1] * tx) * (1 - ty) +
    (field[k + cols] * (1 - tx) + field[k + cols + 1] * tx) * ty;
}

function surfaceHeight(h: number, coast: number): number {
  const rise = Math.max(0, Math.min(1, (h - SURFACE_FROM) / 0.8));
  const inland = Math.max(0, coast - COAST_INSET);
  const taper = Math.min(1, inland / 7);
  // The smooth foot meets the existing ground without a raised cut edge.
  return Math.min(SURFACE_PEAK * Math.pow(rise, 1.22) * taper * taper * (3 - 2 * taper), inland * 0.75);
}

function interpolateSurface(a: SurfaceVertex, b: SurfaceVertex, t: number): SurfaceVertex {
  return {
    x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t,
    h: a.h + (b.h - a.h) * t, coast: a.coast + (b.coast - a.coast) * t,
    z: a.z + (b.z - a.z) * t,
    snow: a.snow + (b.snow - a.snow) * t,
  };
}

/** Clipping an edge always interpolates its same two shared vertices. The
 * neighbour therefore gets the exact same edge, including at chunk seams. */
function clipSurface(
  polygon: readonly SurfaceVertex[], distance: (v: SurfaceVertex) => number, groundEdge = false,
): SurfaceVertex[] {
  const result: SurfaceVertex[] = [];
  if (polygon.length === 0) return result;
  let a = polygon[polygon.length - 1], da = distance(a);
  for (const b of polygon) {
    const db = distance(b);
    if ((da >= 0) !== (db >= 0)) {
      const edge = interpolateSurface(a, b, da / (da - db));
      // Height and coast contours meet the unraised ground. X-slab clips use
      // the already projected plane instead, so they must retain linear z.
      if (groundEdge) edge.z = 0;
      result.push(edge);
    }
    if (db >= 0) result.push(b);
    a = b;
    da = db;
  }
  return result;
}

function surfaceBounds(points: readonly Point[]): Bounds {
  return {
    x0: Math.min(...points.map((p) => p[0])), y0: Math.min(...points.map((p) => p[1])),
    x1: Math.max(...points.map((p) => p[0])), y1: Math.max(...points.map((p) => p[1])),
  };
}
function overlaps(a: Bounds, b: Bounds): boolean {
  return a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;
}

/** Batch equal materials only across disjoint intervening faces. Unlike
 * grouping the whole range by colour, this preserves projected occlusion. */
function surfaceBatches(faces: SurfaceFace[]): SurfaceBatch[] {
  const batches: SurfaceBatch[] = [];
  faces.sort((a, b) => a.depth - b.depth);
  for (const face of faces) {
    let target: SurfaceBatch | undefined;
    for (let i = batches.length - 1; i >= 0; i--) {
      const candidate = batches[i];
      if (candidate.ink === face.ink) { target = candidate; break; }
      if (overlaps(candidate, face)) break;
    }
    if (target) {
      target.polygons.push(face.points);
      target.x0 = Math.min(target.x0, face.x0); target.y0 = Math.min(target.y0, face.y0);
      target.x1 = Math.max(target.x1, face.x1); target.y1 = Math.max(target.y1, face.y1);
    } else {
      batches.push({ ...face, polygons: [face.points] });
    }
  }
  return batches;
}

function surfacePath(polygons: readonly (readonly Point[])[]): string {
  return polygons.map((points) => `M${points.map(([x, y]) => `${n(x)} ${n(y)}`).join('L')}Z`).join('');
}

/** The overview keeps the same ordered facets but paints them only once.
 * Canvas paths are synchronous, so there is no SVG-image decode to race the
 * first map frame. The finished bitmap is masked once against the coast;
 * individual facets never carry that expensive complex clipping region. */
function surfaceRaster(chunks: readonly SurfaceChunk[]): string | null {
  if (typeof document === 'undefined' || typeof Path2D === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(VIEW.w * 2);
  canvas.height = Math.ceil(VIEW.h * 2);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const sx = canvas.width / VIEW.w, sy = canvas.height / VIEW.h;
  ctx.setTransform(sx, 0, 0, sy, -VIEW.x * sx, -VIEW.y * sy);
  ctx.lineWidth = SURFACE_STROKE;
  ctx.lineJoin = 'round';
  for (const chunk of chunks) {
    for (const batch of surfaceBatches(chunk.faces)) {
      const fill = SURFACE_COLORS[batch.ink];
      const outline = new Path2D(surfacePath(batch.polygons));
      ctx.fillStyle = fill;
      ctx.strokeStyle = fill;
      ctx.fill(outline);
      ctx.stroke(outline);
    }
  }
  ctx.globalCompositeOperation = 'destination-in';
  ctx.fillStyle = '#ffffff';
  ctx.fill(new Path2D(coastPath()));
  ctx.globalCompositeOperation = 'source-over';
  const data = canvas.toDataURL('image/png');
  // This detached canvas is only scratch space; retain the encoded image,
  // not its full backing store. One cached URL lives for the map's lifetime.
  canvas.width = 0;
  canvas.height = 0;
  let url = data;
  if (typeof URL.createObjectURL === 'function') {
    const binary = atob(data.slice(data.indexOf(',') + 1));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    url = URL.createObjectURL(new Blob([bytes], { type: 'image/png' }));
  }
  return `<g class="wm-mountain-surface" style="--wm-k:1" pointer-events="none">` +
    `<image href="${url}" x="${n(VIEW.x)}" y="${n(VIEW.y)}" ` +
    `width="${n(VIEW.w)}" height="${n(VIEW.h)}" preserveAspectRatio="none"/></g>`;
}

const surfaceCache: [string | null, string | null] = [null, null];

/**
 * Continuous campaign relief, cached once per level of detail. Uses only the published elevation
 * raster: provinces never clip, restart or change the mesh. The chart supplies
 * its existing `wm-land` coast clip alongside its other shared definitions.
 *
 * Chunks are depth bands split into disjoint projected x slabs. They render
 * north to south; faces within a band retain that order too. A lifted face can
 * overlap many bands, so culling bounds are measured AFTER projection and
 * include the small crack-closing stroke. Adjacent x slabs cannot occlude one
 * another, because polygons are split at their common vertical boundary.
 * The far grid samples the same elevation and coast fields at three times the
 * pitch. It is a complete connected surface too; omitting scattered faces
 * would leave gaps and destroy the ranges at the scale of the realm.
 * Browsers bake that overview into one image; headless callers receive its
 * vector form so the same geometry and culling bounds remain inspectable.
 */
export function mountainSurface(far = false): string {
  const lod = far ? 1 : 0;
  const cached = surfaceCache[lod];
  if (cached !== null) return cached;
  const { cols, rows, cell, originX, originY } = RASTER;
  const coast = coastDistances();
  const ice = Float32Array.from(RASTER.owner, (p) => p >= 0 && provinceDef(p).terrain === 'ice' ? 1 : 0);
  const step = far ? SURFACE_FAR_STEP : SURFACE_STEP;
  const chunkWidth = step * 12, depthBand = step * 1.5;
  const nx = Math.ceil(cols * cell / step);
  const ny = Math.ceil(rows * cell / step);
  const stepX = cols * cell / nx, stepY = rows * cell / ny;
  const chunks = new Map<string, SurfaceChunk>();

  const gridRow = (row: number): SurfaceVertex[] => Array.from({ length: nx + 1 }, (_, column) => {
    const x = originX + column * stepX +
      (column > 0 && column < nx ? (hash2(column, row, 827) - 0.5) * stepX * 0.36 : 0);
    const y = originY + row * stepY +
      (row > 0 && row < ny ? (hash2(column, row, 941) - 0.5) * stepY * 0.36 : 0);
    const h = sampleSurface(RASTER.height, x, y);
    const d = sampleSurface(coast, x, y);
    // Sample before warp/elevation projection, using the shore layer's own
    // height, latitude and snowline. Blend the ice flag over shared cells so
    // a province boundary never makes a sharp line across a continuous face.
    const fx = (x - originX) / cell, fy = (y - originY) / cell;
    const cover = snowAt(h, fx, fy, y, false);
    const snow = cover + (snowAt(h, fx, fy, y, true) - cover) * sampleSurface(ice, x, y);
    const p = warp({ x, y });
    return { x: p.x, y: p.y, h, coast: d, z: surfaceHeight(h, d), snow };
  });

  const addFace = (a: SurfaceVertex, b: SurfaceVertex, c: SurfaceVertex): void => {
    if (Math.max(a.h, b.h, c.h) <= SURFACE_FROM ||
      Math.max(a.coast, b.coast, c.coast) <= COAST_INSET) return;
    let polygon = clipSurface([a, b, c], (v) => v.h - SURFACE_FROM, true);
    polygon = clipSurface(polygon, (v) => v.coast - COAST_INSET, true);
    if (polygon.length < 3) return;

    const u = polygon[0], v = polygon[1], w = polygon[2];
    const ax = v.x - u.x, ay = v.y - u.y, az = v.z - u.z;
    const bx = w.x - u.x, by = w.y - u.y, bz = w.z - u.z;
    const normalX = ay * bz - az * by, normalY = az * bx - ax * bz;
    const normalZ = ax * by - ay * bx;
    // A northward projection looks down from the south. Back faces carry no
    // visible ground and omitting them avoids painting inverted gully slivers.
    if (normalY + normalZ <= 0.0001) return;
    const length = Math.hypot(normalX, normalY, normalZ);
    if (length < 0.0001) return;
    const lit = (-normalX * 0.57 - normalY * 0.57 + normalZ * 0.59) / length;
    const light = Math.max(0, Math.min(5, Math.round((lit + 0.24) * 4.8)));
    const altitude = polygon.reduce((sum, p) => sum + p.h, 0) / polygon.length;
    const material = altitude < 0.53 ? 0 : altitude < 0.68 ? 1 : altitude < 0.86 ? 2 : 3;
    const cover = polygon.reduce((sum, p) => sum + p.snow, 0) / polygon.length;
    // Steep crags hold less snow; gentler frozen slopes retain a white mantle.
    const snow = Math.round(cover * (0.7 + 0.3 * Math.max(0, normalZ / length)) * 3);
    const ink = (snow * SURFACE_INK.length + material) * 6 + light;
    const depth = polygon.reduce((sum, p) => sum + p.y, 0) / polygon.length;
    const band = Math.floor(depth / depthBand);
    const left = Math.floor(Math.min(...polygon.map((p) => p.x)) / chunkWidth);
    const right = Math.floor(Math.max(...polygon.map((p) => p.x)) / chunkWidth);
    for (let column = left; column <= right; column++) {
      let piece = clipSurface(polygon, (p) => p.x - column * chunkWidth);
      piece = clipSurface(piece, (p) => (column + 1) * chunkWidth - p.x);
      if (piece.length < 3) continue;
      const points: Point[] = piece.map((p) => [p.x, p.y - p.z]);
      const bounds = surfaceBounds(points);
      if (bounds.x1 - bounds.x0 < 0.001 || bounds.y1 - bounds.y0 < 0.001) continue;
      const key = `${band}:${column}`;
      let chunk = chunks.get(key);
      if (!chunk) {
        chunk = { row: band, column, faces: [] };
        chunks.set(key, chunk);
      }
      chunk.faces.push({ points, ...bounds, depth, ink });
    }
  };

  let north = gridRow(0);
  for (let row = 0; row < ny; row++) {
    const south = gridRow(row + 1);
    for (let column = 0; column < nx; column++) {
      const a = north[column], b = north[column + 1], c = south[column + 1], d = south[column];
      if (hash2(column, row, 613) < 0.5) { addFace(a, b, c); addFace(a, c, d); }
      else { addFace(a, b, d); addFace(b, c, d); }
    }
    north = south;
  }

  const ordered = [...chunks.values()].sort((a, b) => a.row - b.row || a.column - b.column);
  if (far) {
    const raster = surfaceRaster(ordered);
    if (raster !== null) {
      surfaceCache[lod] = raster;
      return raster;
    }
  }
  const content = ordered
    .map((chunk) => {
      const batches = surfaceBatches(chunk.faces);
      // Include the stroke radius and coordinate rounding in culling bounds.
      const edge = SURFACE_STROKE / 2 + 0.02;
      const bounds = {
        x0: Math.min(...batches.map((b) => b.x0)) - edge,
        y0: Math.min(...batches.map((b) => b.y0)) - edge,
        x1: Math.max(...batches.map((b) => b.x1)) + edge,
        y1: Math.max(...batches.map((b) => b.y1)) + edge,
      };
      const body = batches.map((batch) => {
        const fill = SURFACE_COLORS[batch.ink];
        const d = surfacePath(batch.polygons);
        return `<path d="${d}" style="fill:${fill};stroke:${fill};stroke-width:${SURFACE_STROKE};stroke-linejoin:round"/>`;
      }).join('');
      return `<g data-mountain-chunk="${chunk.row}:${chunk.column}" ` +
        `data-x0="${n(bounds.x0)}" data-y0="${n(bounds.y0)}" ` +
        `data-x1="${n(bounds.x1)}" data-y1="${n(bounds.y1)}">${body}</g>`;
    }).join('');
  const surface = `<g class="wm-mountain-surface" style="--wm-k:1" pointer-events="none" clip-path="url(#wm-land)">${content}</g>`;
  surfaceCache[lod] = surface;
  return surface;
}
